import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { useAccounts } from '../accounts/useAccounts'
import { BUDGET_CURRENCY, toEnvelopes, type Envelope } from './budgetWire'

export type { BudgetPeriod, Envelope, EnvelopeProgress } from './budgetWire'
// The predicates and the rounded percentage other modules use, so they import this one file and never budgetRules.
export { BUDGET_CURRENCY }
// Where the budget module lives, for a link out of another screen.
export { BUDGET_PATH, budgetDetailPath } from './budgetPaths'
export { isClose, isOver, isUntouched, shownPct } from './budgetRules'

export class BudgetsError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`Budgets request failed with ${status}`)
    this.name = 'BudgetsError'
    this.status = status
  }
}

async function listBudgets(): Promise<Envelope[]> {
  const { data, error, response } = await api.GET('/api/v1/budgets')
  if (error !== undefined || data === undefined) throw new BudgetsError(response.status)
  return toEnvelopes(data)
}

/**
 * Every budget the user has (active and paused, server order), parsed once.
 * This is the module's public read hook: M2 Overview's budget widget consumes it.
 *
 * Synced surface (E5): `stale` with the accounts' oldest `last_synced_at` when
 * `useAccounts()` is stale; `empty` when the user has no budgets at all;
 * `error` with "Try again" on a request or contract failure. An accounts
 * failure or load never blocks or stales the budget page.
 *
 * Consumers use the predicates re-exported here (`isOver`, `isClose`,
 * `isUntouched`, and `shownPct`, the rounding `EnvelopeBar` announces) rather than re-deriving them; `budgetRules.ts` stays internal.
 */
export function useBudgets({ enabled = true }: { enabled?: boolean } = {}): AsyncState<Envelope[]> {
  const query = useQuery({ queryKey: keys.budgets.list(), queryFn: listBudgets, enabled })
  const accounts = useAccounts()
  const state = fromQuery(query, {
    message: () => 'Budgets could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (envelopes) => envelopes.length === 0,
  })
  if (state.status === 'success' && accounts.status === 'stale') {
    return { status: 'stale', data: state.data, asOf: accounts.asOf }
  }
  return state
}

/**
 * One envelope by id, selected from the list cache (never `GET /budgets/{id}`: a second request
 * lets the detail and the list disagree after an edit — the `CategoryDetail` precedent). `empty`
 * means the envelope is not in the list: deleted elsewhere, or a bad link.
 */
export function envelopeState(list: AsyncState<Envelope[]>, id: string | undefined): AsyncState<Envelope> {
  if (list.status === 'loading' || list.status === 'error' || list.status === 'empty') return list
  const found = id === undefined ? undefined : list.data.find((e) => e.id === id)
  if (found === undefined) return { status: 'empty' }
  return list.status === 'stale' ? { status: 'stale', data: found, asOf: list.asOf } : { status: 'success', data: found }
}

/** What the budget module can say about a category for this month, with the reason when it cannot. */
export type CategoryBudget =
  | { kind: 'monthly'; envelope: Envelope }
  | { kind: 'none' }
  /** An envelope claims the category but is not an active monthly one: weekly, paused, or several. `envelope` is null for several. */
  | { kind: 'other'; envelope: Envelope | null }

/**
 * The one active monthly envelope whose category it is: it answers "how is this category doing THIS
 * MONTH". A weekly or yearly envelope's progress is for its own window and a paused one has none, so
 * those are `other`; so are several claimants, since picking one would be a guess and adding them money
 * arithmetic the server did not do. `none` means no envelope names the category at all.
 */
export function budgetForCategory(envelopes: readonly Envelope[], categoryId: string): CategoryBudget {
  const claims = envelopes.filter((e) => e.categoryId === categoryId)
  if (claims.length === 0) return { kind: 'none' }
  const monthly = claims.filter((e) => e.isActive && e.period === 'monthly')
  const [only] = monthly
  if (monthly.length === 1 && only !== undefined) return { kind: 'monthly', envelope: only }
  return { kind: 'other', envelope: claims.length === 1 ? (claims[0] ?? null) : null }
}
